import {Clone} from "js-vextensions";
import {observer_mgl, RunInAction} from "mobx-graphlink";
import React from "react";
import {VMenuItem, VMenuStub} from "react-vmenu";
import {ShowMessageBox} from "react-vmessagebox";
import {ES} from "web-vcore";
import {DeleteMapObject, UpdateMapObject} from "../../../../Server/Commands/MapObjectCommands.js";
import {GetMapObject, GetMapObjects} from "../../../../Server/DB/mapObjects.js";
import {MapObject} from "../../../../Server/DB/mapObjects/@MapObject.js";
import {ObjPath} from "../../../../Server/DB/mapObjects/@MapObject/ObjPath.js";
import {ResolveObjPosInWorld, ResolveObjRotInWorld} from "../../../../Server/DB/mapObjects/$mapObject.js";
import {DMap} from "../../../../Server/DB/maps/@Map.js";
import {store} from "../../../../Store/index.js";
import {GetCurrentMapControls} from "../../../../Store/main/maps.js";
import {MapState} from "../../../../Store/main/maps/@MapState.js";
import {liveSkin} from "../../../../Utils/Styles/StyleManager.js";
import {TransformObjLocalPointToWorldOffset_UsingRotationOnly} from "../../../../Utils/ThreeJS/SpaceUtils.js";
import {Vec2, Vec3} from "../../../../Utils/ThreeJS/VectorStructs.js";
import {MapTool} from "../Toolbox/Tools/@Tool.js";
import {PathModifySubtool} from "../Toolbox/Tools/PathModify.js";
import {ShowObjOverlapsDialog} from "./ContextMenu/ObjOverlapsDialog.js";

export const MapSurface_MenuStub = observer_mgl((props: {
	map: DMap, mapState: MapState, delayEventHandler?: boolean,
})=>{
	const {delayEventHandler, ...rest} = props;
	//const [point_world, setPoint_world] = useState<Vec3|n>(null);
	// We have to use a persistent liveState object, because VMenuStub only transfers children+props at time of right-click.
	// (ie. re-renders of the menu-items don't transmit to the actual rendered menu)
	const liveState = {rightClickedPoint_world: null as Vec3|n};
	return (
		<VMenuStub delayEventHandler={delayEventHandler ?? false} preOpen={e=>{
			//if (e.handled) return false;
			const controls = GetCurrentMapControls();
			const rightClickedPoint_world = controls?.DOM?.ClientPosToWorldPos(new Vec2(e.clientX, e.clientY)) ?? null;
			//setPoint_world(rightClickedPoint_world);
			liveState.rightClickedPoint_world = rightClickedPoint_world;
			return true;
		}}>
			<MapSurface_Menu {...rest} liveState={liveState}/>
		</VMenuStub>
	);
});
const MapSurface_Menu = observer_mgl((props: {
	map: DMap, mapState: MapState, liveState: {rightClickedPoint_world: Vec3|n,
}})=>{
	const {map, mapState, liveState} = props;
	const {rightClickedPoint_world} = liveState;

	const objects = GetMapObjects(map.id);
	const objectsToDelete = mapState.selectedObjects.map(objID=>objects.find(a=>a.id == objID));
	const deletionError = GetDeletionError(map, objectsToDelete);

	const headerStyle = ES(liveSkin.Style_VMenuItem(), {opacity: 1});

	// Path-edit "delete point" support: when the path-modify tool is active with the
	// "move points" subtool, and the right-click landed on one of the red point dots,
	// offer a "Delete path point" action.
	const pathDeletePointInfo = GetPathDeletePointInfo(map, mapState, rightClickedPoint_world);

	return <>
		<VMenuItem text="Show overlapping objects" style={headerStyle} onClick={()=>{
			if (rightClickedPoint_world == null) return;
			ShowObjOverlapsDialog({map, mapState, objects, point_world: rightClickedPoint_world});
		}}/>
		{pathDeletePointInfo && <VMenuItem text={`Delete path point ${pathDeletePointInfo.pointIndex + 1}`} style={headerStyle} onClick={async ()=>{
			const {obj, objPath, pointIndex} = pathDeletePointInfo;
			const newPaths = Clone(obj.paths) as ObjPath[];
			const path = newPaths[obj.paths.indexOf(objPath)];
			path.points = path.points.filter((_, i)=>i != pointIndex);
			await new UpdateMapObject({id: obj.id, updates: {paths: newPaths}}).RunOnServer();
		}}/>}
		<VMenuItem text={objectsToDelete.length ? `Delete object${objectsToDelete.length > 1 ? "s" : ""} (${objectsToDelete.length})` : "Delete"}
			enabled={deletionError == null} title={deletionError} style={headerStyle}
			onClick={()=>{
				ShowMessageBox({
					title: `Delete all ${objectsToDelete.length} selected`, cancelButton: true,
					message: [
						`Delete the ${objectsToDelete.length} selected objects?`,
						...objectsToDelete.map(obj=>obj ? `- "${obj.name}"` : "<object not found>"),
					].join("\n"),
					onOK: async()=>{
						for (const objID of mapState.selectedObjects) {
							await new DeleteMapObject({id: objID}).RunOnServer();
						}
						RunInAction("MapSurface_Menu.delete", ()=>{
							mapState.selectedObjects = [];
							mapState.activeObject = null;
						});
					},
				});
			}}/>
	</>;
});

function GetPathDeletePointInfo(map: DMap, mapState: MapState, rightClickedPoint_world: Vec3|n) {
	if (rightClickedPoint_world == null) return null;
	// Only offer the "delete point" action when the path-modify tool is active with the "move points" subtool.
	const tools = store.main.maps.tools;
	if (tools.activeTool != MapTool.pathModify) return null;
	const pathModifyState = tools.toolStates.pathModify;
	if (pathModifyState.subtool != PathModifySubtool.movePoints) return null;

	const objID = mapState.activeObject;
	if (objID == null) return null;
	const obj = GetMapObject(objID, map.id);
	if (obj == null) return null;
	const objPath = ObjPath.GetSelected(obj);
	if (objPath == null || objPath.points.length == 0) return null;
	const objPos = ResolveObjPosInWorld.CatchBail(null, obj);
	if (objPos == null) return null;
	const objRot = ResolveObjRotInWorld.CatchBail(null, obj);
	if (objRot == null) return null;

	const controls = GetCurrentMapControls();
	const controls_DOM = controls?.DOM;
	if (controls_DOM == null) return null;
	const maxDist = 5 * controls_DOM.OnePixelAsMeters().x; // 5 pixels, converted to meters (same threshold as PathModify.GetNearestPointIfClose)

	let closestIndex: number|n = null;
	let closestDist = Infinity;
	for (let i = 0; i < objPath.points.length; i++) {
		const point_world = objPos.Plus(TransformObjLocalPointToWorldOffset_UsingRotationOnly(Vec2.FromSimple(objPath.points[i]), objRot).ToVec3(0));
		const dist = rightClickedPoint_world.ToVec2().DistanceTo(point_world.ToVec2());
		if (dist < closestDist && dist <= maxDist) {
			closestDist = dist;
			closestIndex = i;
		}
	}
	if (closestIndex == null) return null;
	return {obj, objPath, pointIndex: closestIndex};
}

function GetDeletionError(map: DMap, objectsToDelete_maybeNull: (MapObject|n)[]) {
	if (objectsToDelete_maybeNull.length == 0) return "Cannot delete when no objects are selected.";
	if (objectsToDelete_maybeNull.some(a=>a == null)) return "Cannot delete a selection containing ids of objects that don't exist. (defensive)";
	const objectsToDelete = objectsToDelete_maybeNull as MapObject[];

	const objects = GetMapObjects(map.id);
	for (const obj of objectsToDelete) {
		const children = objects.filter(a=>a.parent == obj.id);
		const allChildrenAreAlsoBeingDeleted = children.every(child=>objectsToDelete.find(a=>a.id == child.id));
		if (!allChildrenAreAlsoBeingDeleted) return `Cannot delete object "${obj.name}" because it has children that are not part of the deletion selection.`;
	}
	return null;
}